import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const ReviewsSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [showReviewForm, setShowReviewForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 5,
    review: '',
    project: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const reviews = [
    {
      name: 'Michael Thompson',
      project: 'Concrete Driveway',
      rating: 5,
      text: 'Outstanding work! The team at A&B Concrete transformed our driveway. Professional, punctual, and the quality is exceptional. Highly recommend their services.',
      image: 'https://i.pravatar.cc/150?img=12',
    },
    {
      name: 'Sarah Martinez',
      project: 'Patio Installation',
      rating: 5,
      text: 'We absolutely love our new patio! The crew was incredibly skilled and paid attention to every detail. They completed the project on time and within budget.',
      image: 'https://i.pravatar.cc/150?img=45',
    },
    {
      name: 'David Johnson',
      project: 'Paver Walkway',
      rating: 5,
      text: 'Excellent craftsmanship and customer service. Our paver walkway looks amazing and has held up beautifully. Worth every penny!',
      image: 'https://i.pravatar.cc/150?img=33',
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        rating: formData.rating.toString(),
        review: formData.review,
        project: formData.project,
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d4s9lhjrvmocq14jmlfg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', rating: 5, review: '', project: '' });
        setTimeout(() => {
          setShowReviewForm(false);
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="reviews" className="bg-[#F8F6F3] py-24 lg:py-32" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-brand-green text-sm font-bold uppercase tracking-widest mb-4">TESTIMONIALS</p>
          <h2 className="text-5xl lg:text-6xl font-black text-gray-900">What Our Clients Say</h2>
        </motion.div>

        {/* Review Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {reviews.map((review, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow relative"
            >
              {/* Client Photo */}
              <div className="absolute -top-6 right-8 w-16 h-16 rounded-full overflow-hidden border-4 border-white shadow-lg">
                <img src={review.image} alt={review.name} className="w-full h-full object-cover" />
              </div>

              {/* Rating */}
              <div className="flex gap-1 mb-6">
                {[...Array(review.rating)].map((_, i) => (
                  <div key={i} className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-star-fill text-brand-green text-xl"></i>
                  </div>
                ))}
              </div>

              {/* Review Text */}
              <p className="text-gray-700 text-base leading-relaxed mb-8">{review.text}</p>

              {/* Client Info */}
              <div>
                <h4 className="font-bold text-gray-900 text-base">{review.name}</h4>
                <p className="text-gray-500 text-sm italic">{review.project}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Write Review Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center"
        >
          <button
            onClick={() => setShowReviewForm(true)}
            className="bg-white text-gray-900 px-12 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all shadow-md whitespace-nowrap cursor-pointer"
          >
            WRITE YOUR REVIEW
          </button>
        </motion.div>
      </div>

      {/* Review Form Modal */}
      <AnimatePresence>
        {showReviewForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={() => setShowReviewForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-3xl font-black text-gray-900">Share Your Experience</h3>
                <button
                  onClick={() => setShowReviewForm(false)}
                  className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl text-gray-600"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit} data-readdy-form id="review-form">
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 outline-none transition-all text-sm"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 outline-none transition-all text-sm"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Project Type</label>
                    <input
                      type="text"
                      name="project"
                      value={formData.project}
                      onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 outline-none transition-all text-sm"
                      placeholder="e.g., Concrete Driveway, Patio Installation"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Rating</label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setFormData({ ...formData, rating: star })}
                          className="w-10 h-10 flex items-center justify-center cursor-pointer"
                        >
                          <i
                            className={`text-3xl ${
                              star <= formData.rating ? 'ri-star-fill text-brand-green' : 'ri-star-line text-gray-300'
                            }`}
                          ></i>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Your Review</label>
                    <textarea
                      name="review"
                      value={formData.review}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setFormData({ ...formData, review: e.target.value });
                        }
                      }}
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 outline-none transition-all resize-none text-sm"
                      placeholder="Tell us about your experience..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.review.length}/500 characters</p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg text-sm">
                      Thank you for your review! It has been submitted successfully.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">
                      Something went wrong. Please try again.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-brand-green text-white py-4 rounded-full font-semibold hover:bg-brand-green/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Review'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ReviewsSection;